import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, exhaustMap, map, of, switchMap, tap } from "rxjs";
import { ReportsApiService } from "../data-access/reports-api.service";
import { ReportsActions } from "./reports.actions";
import { reportsFeature } from "./reports.reducer";
import { Store } from "@ngrx/store";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";

@Injectable()
export class ReportsEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(ReportsApiService);
    private readonly store = inject(Store);

    readonly open$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.pageOpened),
            map(() => ReportsActions.pageRequested({ token: "", pageIndex: 0 }))
        )
    );

    readonly load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.pageRequested),
            switchMap(({ token, pageIndex }) => {
                const state = this.store.selectSignal(
                    reportsFeature.selectReportsState
                )();
                const request = this.api.loadCompanyReports(
                    state.clientId,
                    state.pageSize,
                    token
                );
                return (
                    state.loaded ? request : request.pipe(withSkeletonTiming())
                ).pipe(
                    map(({ reports, nextToken }) =>
                        ReportsActions.pageLoaded({
                            reports,
                            nextToken,
                            token,
                            pageIndex,
                        })
                    ),
                    catchError(() => of(ReportsActions.pageLoadFailed()))
                );
            })
        )
    );

    readonly download$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.downloadRequested),
            exhaustMap(({ report }) =>
                this.api.download(report).pipe(
                    tap((blob) => this.save(blob, this.api.filename(report))),
                    map(() => ReportsActions.downloadSucceeded()),
                    catchError(() => of(ReportsActions.downloadFailed()))
                )
            )
        )
    );

    private save(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }
}
